export type Language = 'nl' | 'be-nl' | 'en' | 'fr';

export type DocumentLanguage = Exclude<Language, 'be-nl'>;

type OfficeCountry = 'NL' | 'BE' | 'FR';

const LANGUAGE_GROUPS_BY_COUNTRY: Record<OfficeCountry, Language[][]> = {
  NL: [ [ 'en' ], [ 'nl' ] ],
  BE: [ [ 'en' ], [ 'be-nl', 'nl' ], [ 'fr' ] ],
  FR: [ [ 'en' ], [ 'fr' ] ],
};

const OFFICE_LANGUAGE_BY_COUNTRY: Record<OfficeCountry, Language> = {
  NL: 'nl',
  BE: 'be-nl',
  FR: 'fr',
};

export function toDocumentLanguage(language: Language): DocumentLanguage {
  return language === 'be-nl' ? 'nl' : language;
}

export function filterLanguagesByCountry(
  countryCode: string | null | undefined,
  availableLanguages: Language[]
): Language[] {
  if (!isOfficeCountry(countryCode)) return availableLanguages;

  return LANGUAGE_GROUPS_BY_COUNTRY[countryCode]
    .map(group => group.find(language => availableLanguages.includes(language)))
    .filter((language): language is Language => language !== undefined);
}

function isOfficeCountry(countryCode: string | null | undefined): countryCode is OfficeCountry {
  return !!countryCode && Object.keys(LANGUAGE_GROUPS_BY_COUNTRY).includes(countryCode);
}

export function resolveLanguageForCountry(
  language: Language,
  allowedLanguages: Language[]
): Language {
  if (allowedLanguages.includes(language)) return language;

  const sameFamily = allowedLanguages.find(
    allowed => toDocumentLanguage(allowed) === toDocumentLanguage(language)
  );

  return sameFamily ?? allowedLanguages[0] ?? language;
}

export function getOfficeLanguage(countryCode: string | null | undefined): Language | null {
  return isOfficeCountry(countryCode) ? OFFICE_LANGUAGE_BY_COUNTRY[countryCode] : null;
}
